import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/services";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";
import CursorGlowCard from "@/components/brand/CursorGlowCard";

export default function ClaimServicesGrid() {
  return (
    <section
      data-testid="services-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-deeper"
    >
      <AmbientLight size={1000} color="blue" intensity={0.35} style={{ top: "15%", right: "-10%" }} />
      <AmbientLight size={700} color="gold" intensity={0.15} style={{ bottom: "5%", left: "-8%" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="07" label="Claim Services" className="mb-14" />

        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-24 gap-10">
          <div className="max-w-2xl">
            <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.2vw,4.75rem)] leading-[0.96] tracking-[-0.045em]">
              Every category,
              <br />
              <span className="gold-metallic font-display font-light">one disciplined approach.</span>
            </h2>
            <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold to-transparent" />
          </div>
          <Link
            to="/claim-services"
            data-testid="services-view-all"
            className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.22em] uppercase text-white/70 hover:text-gold transition-colors group self-start lg:self-end"
          >
            <span className="h-px w-8 bg-gold/40 group-hover:bg-gold transition-colors" />
            All 15 Services
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <div className="space-y-28 lg:space-y-32">
          {SERVICE_CATEGORIES.map((cat, ci) => {
            const services = SERVICES.filter((s) => s.category === cat.id);
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`category-${cat.id}`}
              >
                {/* Category masthead */}
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-12 lg:mb-14">
                  <div className="lg:col-span-7">
                    <div className="flex items-baseline gap-4 mb-5">
                      <span className="font-mono text-[0.6rem] gold-metallic tracking-[0.32em]">
                        {String(ci + 1).padStart(2, "0")} / {String(SERVICE_CATEGORIES.length).padStart(2, "0")}
                      </span>
                      <div className="h-px flex-1 bg-gradient-to-r from-gold/40 via-glow/30 to-transparent max-w-[180px]" />
                    </div>
                    <h3 className="font-display-tight text-white text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.02] tracking-[-0.04em] font-light">
                      {cat.label}
                    </h3>
                    <p className="mt-5 text-ag-text-secondary text-base lg:text-[1.05rem] leading-[1.7] font-light max-w-xl">
                      {cat.description}
                    </p>
                  </div>
                  <div className="lg:col-span-5 lg:text-right">
                    <span className="font-mono text-[0.62rem] text-ag-text-muted tracking-[0.28em] uppercase">
                      {String(services.length).padStart(2, "0")} {services.length === 1 ? "Service" : "Services"}
                    </span>
                  </div>
                </div>

                {/* Service cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                  {services.map((s, i) => (
                    <motion.div
                      key={s.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.55, delay: i * 0.05 }}
                    >
                      <CursorGlowCard
                        as={Link}
                        to={`/claim-services/${s.slug}`}
                        data-testid={`service-card-${s.slug}`}
                        className="block p-9 lg:p-10 h-full group hover:border-gold/30"
                      >
                        <div className="relative h-full flex flex-col">
                          <span className="font-mono text-[0.58rem] gold-metallic tracking-[0.32em] mb-6">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <h4 className="font-display text-xl lg:text-2xl text-white group-hover:gold-metallic group-hover:text-transparent transition-colors mb-4 font-light tracking-[-0.025em] leading-[1.2]">
                            {s.title}
                          </h4>
                          <p className="text-[0.92rem] text-ag-text-secondary leading-[1.7] font-light line-clamp-3 flex-1">
                            {s.short}
                          </p>
                          <div className="mt-8 pt-5 border-t border-white/[0.06] inline-flex items-center justify-between gap-2 font-mono text-[0.62rem] tracking-[0.26em] uppercase text-gold/70 group-hover:text-gold transition-colors">
                            <span>Explore</span>
                            <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                          </div>
                        </div>
                      </CursorGlowCard>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
